import {
  useAssetFailureList,
  useCreateAssetFailure,
  useDeleteFailureMode,
  useUpdateAssetFailure,
} from "@features/ax/settings/assetClassification/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Separator } from "@shared/ui/separator";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Edit, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { EditFailureModeDialog } from "./editFailureModeDialog";

interface FailureModeManagementProps {
  selectedAssetType: any | null;
  showFailureModes: boolean;
  onClose: () => void;
}

export const FailureModeManagement: React.FC<FailureModeManagementProps> = ({
  selectedAssetType,
  showFailureModes,
  onClose,
}) => {
  const { toast } = useToast();
  const [editingFailureMode, setEditingFailureMode] = useState<any | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: failureModes } = useAssetFailureList(selectedAssetType?.code);

  const { mutateAsync: createAssetFailure, isPending: isCreating } =
    useCreateAssetFailure();

  const { mutateAsync: updateAssetFailure, isPending: isUpdating } =
    useUpdateAssetFailure();

  const { mutateAsync: deleteFailureMode, isPending: isTypeDeleting } =
    useDeleteFailureMode();

  // Form field definitions (shared between add and edit)
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        type: "text",
        label: "Failure Mode Name",
        group: "Failure Mode Details",
        placeholder: "Enter failure mode name...",
        required: true,
        groupColorNeeded: false,
        className: "col-span-1 mt-3",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        group: "Failure Mode Details",
        placeholder: "Enter description...",
        rows: 2,
        required: false,
        className: "col-span-1 mt-3",
      },
    ],
    []
  );

  // Default form values
  const defaultFormValues = {
    name: "",
    description: "",
  };

  // Create form instances with proper default values
  const addForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: defaultFormValues,
  });

  const editForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: defaultFormValues,
  });

  // Reset add form when dialog opens
  useEffect(() => {
    if (showFailureModes) {
      addForm.reset(defaultFormValues);
    }
  }, [showFailureModes]);

  // Handle edit mode - populate form when editing starts
  useEffect(() => {
    if (editingFailureMode) {
      const editData = {
        name: editingFailureMode.name || "",
        description: editingFailureMode.description || "",
      };
      editForm.reset(editData);
    }
  }, [editingFailureMode, editForm]);

  // Handle add form submission
  const handleAddSubmit = async (formData: any) => {
    try {
      console.log("Submitting add form:", formData);

      // Validate required fields
      if (!formData.name?.trim()) {
        toast({
          title: "Error",
          description: "Please enter a failure mode name",
          variant: "destructive",
        });
        return;
      }

      // Wait for the mutation to complete
      await createAssetFailure({
        name: formData.name,
        code: selectedAssetType.code + "_" + formData.name,
        remote_utility_id: getRemoteUtilityId(),
        description: formData.description || "",
        asset_type_characteristics: "failure_mode",
      });

      // Reset form only after successful creation
      addForm.reset(defaultFormValues);

      toast({
        title: "Success",
        description: "Failure mode added successfully",
      });
    } catch (error) {
      console.error("Error creating failure mode:", error);
      toast({
        title: "Error",
        description: "Failed to create failure mode",
        variant: "destructive",
      });
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (formData: any) => {
    try {
      if (!formData.name?.trim()) {
        toast({
          title: "Error",
          description: "Please enter a failure mode name",
          variant: "destructive",
        });
        return;
      }

      await updateAssetFailure({
        name: formData.name,
        code: editingFailureMode.code,
        remote_utility_id: getRemoteUtilityId(),
        description: formData.description || "",
        asset_type_characteristics: "failure_mode",
      });

      // Close edit dialog after successful update
      setEditingFailureMode(null);
      setIsDialogOpen(false);

      toast({
        title: "Success",
        description: "Failure mode updated successfully",
      });
    } catch (error) {
      console.error("Error updating failure mode:", error);
      toast({
        title: "Error",
        description: "Failed to update failure mode",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (code: string) => {
    try {
      await deleteFailureMode(code);
      toast({
        title: "Success",
        description: "Failure mode deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting failure mode:", error);
      toast({
        title: "Error",
        description: "Failed to delete failure mode",
        variant: "destructive",
      });
    }
  };

  const handleAddReset = () => {
    addForm.reset(defaultFormValues);
  };

  const handleEditClose = () => {
    setEditingFailureMode(null);
    editForm.reset(defaultFormValues);
    setIsDialogOpen(false);
  };

  const handleStartEdit = (failureMode: any) => {
    setEditingFailureMode(failureMode);
    setIsDialogOpen(true);
  };

  return (
    <Dialog open={showFailureModes} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Manage Failure Modes for: {selectedAssetType?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Failure Mode */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-medium mb-4">Add New Failure Mode</h4>
            <DynamicForm
              fields={formFields}
              form={addForm}
              onSubmit={handleAddSubmit}
              onReset={handleAddReset}
              loading={isCreating}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                submitButtonText: isCreating ? "Adding..." : "Add Failure Mode",
                resetButtonText: "Clear",
                spacing: "comfortable",
                classes: {
                  form: "space-y-4 p-0",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer: "flex justify-end gap-3 pt-4 mt-4",
                  submitButton: isCreating
                    ? "bg-blue-400 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-sm text-gray-500 mt-1",
                  container: "space-y-1",
                },
              }}
            />
          </div>

          <Separator />

          {/* Existing Failure Modes */}
          <div className="space-y-4">
            <h4 className="font-medium">Existing Failure Modes</h4>
            {failureModes?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No failure modes defined yet.
              </p>
            ) : (
              <div className="space-y-2">
                {failureModes?.map((failureMode) => (
                  <div
                    key={failureMode.id}
                    className="border rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <h5 className="font-medium">{failureMode.name}</h5>
                      {failureMode.description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {failureMode.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(failureMode)}
                        disabled={isUpdating || isTypeDeleting}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(failureMode.code)}
                        disabled={isUpdating || isTypeDeleting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <EditFailureModeDialog
          isOpen={isDialogOpen}
          failureMode={editingFailureMode}
          onClose={handleEditClose}
          onSubmit={handleEditSubmit}
          isUpdating={isUpdating}
        />
      </DialogContent>
    </Dialog>
  );
};
